import React, { useMemo } from 'react';

import { useRooftop } from '@store';
import ProductShellLayout from '@features/products/shell/ProductShellLayout';

import { useProductEntry } from '@features/products/useProductEntry';

import GuestRidesCreditPanel from './credit/GuestRidesCreditPanel';
import { isCreditBlocked } from './credit/guestRidesCredit';
import { readGuestPortalEnabled } from './guestPortal';
import { resolveGuestRidesActions } from './resolveGuestRidesActions';
import { resolveGuestRidesTabs } from '@hopdrive/feature-portal-products';
import { PRODUCT_ROUTES } from '@features/products/productRoutes';

const GUEST_RIDES_TABS = resolveGuestRidesTabs(PRODUCT_ROUTES);

/**
 * Guest Rides' mount of the product shell: reads whether the rooftop's guest self-service portal
 * is enabled (the presence condition for Share Code) and hands the tab set and the product's
 * header actions to the layout. The credit panel mounts here rather than in a tab so it stays on
 * screen across Rides and Settings; while it presents a credit block, the shell's own state
 * banner is turned off so the dealer sees one.
 */
const GuestRidesShellLayout: React.FC = () => {
  const rooftop = useRooftop();
  const guestPortalEnabled = readGuestPortalEnabled(rooftop);
  const entry = useProductEntry('guest-rides');

  const actions = useMemo(
    () =>
      resolveGuestRidesActions({
        guestPortalEnabled,
        rooftop: rooftop ? { id: rooftop.id, name: rooftop.name } : null,
      }),
    [guestPortalEnabled, rooftop]
  );

  return (
    <ProductShellLayout
      product='guest-rides'
      tabs={GUEST_RIDES_TABS}
      actions={actions}
      banner={<GuestRidesCreditPanel entry={entry} />}
      suppressStateBanner={isCreditBlocked(entry)}
    />
  );
};

export default GuestRidesShellLayout;
